import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import DivisionStandings from '../components/standings/DivisionStandings';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import { getCompetitionDetails, getFixtures } from '../services/public-api';
import './CompetitionDetailsPage.css';

const CompetitionDetailsPage = () => {
  const { competitionId } = useParams();
  const [competition, setCompetition] = useState(null);
  const [teamCount, setTeamCount] = useState(0);
  const [recentResults, setRecentResults] = useState([]);
  const [upcomingFixtures, setUpcomingFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (competitionId) fetchCompetitionDetails();
  }, [competitionId]);

  const fetchCompetitionDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCompetitionDetails(competitionId);
      setCompetition(data.competition);
      setTeamCount(data.teamCount || 0);

      // Load recent results + upcoming fixtures for first division
      const firstDiv = data.competition?.divisions?.[0]?.divisionId;
      if (firstDiv) {
        const [recentRes, upcomingRes] = await Promise.allSettled([
          getFixtures(competitionId, firstDiv, 'completed'),
          getFixtures(competitionId, firstDiv, 'scheduled'),
        ]);
        if (recentRes.status === 'fulfilled') {
          const sorted = (recentRes.value?.matches || [])
            .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
            .slice(0, 5);
          setRecentResults(sorted);
        }
        if (upcomingRes.status === 'fulfilled') {
          const now = new Date();
          const sorted = (upcomingRes.value?.matches || [])
            .filter(m => new Date(m.scheduledDate) >= now)
            .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
            .slice(0, 5);
          setUpcomingFixtures(sorted);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load competition details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const formatShortDate = (iso) => new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
  });

  const getTypeLabel = (type) => ({ league: 'League', knockout: 'Knockout', bespoke: 'Bespoke' }[type] || type);

  if (loading) return <Container><Loading size="large" /></Container>;
  if (error) return <Container><ErrorMessage message={error} variant="error" onRetry={fetchCompetitionDetails} /></Container>;
  if (!competition) return (
    <Container>
      <div className="not-found">
        <h1>Competition Not Found</h1>
        <Link to="/competitions"><Button variant="primary">View All Competitions</Button></Link>
      </div>
    </Container>
  );

  return (
    <Container>
      <div className="competition-details-page">
        <div className="page-header">
          <Link to="/competitions" className="back-link">← Back to Competitions</Link>
          <h1>{competition.name}</h1>
          <div className="competition-meta">
            <span className={`status-badge status-${competition.status}`}>{competition.status}</span>
            <span className="type-badge">{getTypeLabel(competition.competitionType)}</span>
          </div>
        </div>

        <div className="competition-info">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Start Date</span>
              <span className="info-value">{formatDate(competition.startDate)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">End Date</span>
              <span className="info-value">{formatDate(competition.endDate)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Teams</span>
              <span className="info-value">{teamCount}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Divisions</span>
              <span className="info-value">{competition.divisions?.length || 0}</span>
            </div>
          </div>
          {competition.description && (
            <div className="competition-description">
              <h2>About</h2>
              <p>{competition.description}</p>
            </div>
          )}
        </div>

        <div className="competition-actions">
          <Link to={`/competitions/${competitionId}/fixtures`}>
            <Button variant="secondary">📅 Fixtures</Button>
          </Link>
          <Link to={`/competitions/${competitionId}/standings`}>
            <Button variant="secondary">📊 Standings</Button>
          </Link>
          {competition.competitionType === 'knockout' && (
            <Link to={`/competitions/${competitionId}/bracket`}>
              <Button variant="secondary">🏆 Bracket</Button>
            </Link>
          )}
        </div>

        {/* Recent Results + Upcoming Fixtures side by side */}
        <div className="comp-feed">
          <div className="feed-section">
            <div className="feed-header">
              <h2>Recent Results</h2>
              <Link to={`/competitions/${competitionId}/fixtures`} className="feed-see-all">All →</Link>
            </div>
            {recentResults.length === 0 ? (
              <p className="feed-empty">No results yet.</p>
            ) : (
              <div className="feed-list">
                {recentResults.map(match => (
                  <Link key={match.matchId} to={`/matches/${match.matchId}`} className="feed-result">
                    <span className="feed-date">{formatShortDate(match.scheduledDate)}</span>
                    <span className={match.winnerId === match.team1Id ? 'feed-team winner' : 'feed-team'}>{match.team1Name || 'Team 1'}</span>
                    <span className="feed-score">{match.team1Score} – {match.team2Score}</span>
                    <span className={match.winnerId === match.team2Id ? 'feed-team winner' : 'feed-team'}>{match.team2Name || 'Team 2'}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="feed-section">
            <div className="feed-header">
              <h2>Next Fixtures</h2>
              <Link to={`/competitions/${competitionId}/fixtures`} className="feed-see-all">All →</Link>
            </div>
            {upcomingFixtures.length === 0 ? (
              <p className="feed-empty">No upcoming fixtures.</p>
            ) : (
              <div className="feed-list">
                {upcomingFixtures.map(match => (
                  <div key={match.matchId} className="feed-fixture">
                    <span className="feed-date">{formatShortDate(match.scheduledDate)}</span>
                    <span className="feed-team">{match.team1Name || 'Team 1'}</span>
                    <span className="feed-vs">vs</span>
                    <span className="feed-team">{match.team2Name || 'Team 2'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {competition.divisions && competition.divisions.length > 0 && (
          <div className="standings-section">
            <h2>Standings</h2>
            <DivisionStandings competitionId={competitionId} divisions={competition.divisions} />
          </div>
        )}
      </div>
    </Container>
  );
};

export default CompetitionDetailsPage;
